import { http } from '../../services/http';

/** Phase 12: plan, usage, invoices (manual billing, ADR-053) and self-serve sign-up (ADR-054). */
export interface PublicPlan {
  code: string;
  name: string;
  description: string | null;
  /** null = no price published ("contact us"); never invented. */
  priceAmount: string | null;
  currency: string;
  billingInterval: string;
  trialDays: number;
  features: Record<string, { enabled: boolean; limit: number | null }>;
}

export interface PlanChange {
  id: string;
  fromPlan: { code: string; name: string } | null;
  toPlan: { code: string; name: string };
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  note: string | null;
  requestedBy: string;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  tenant?: { id: string; name: string };
}

export interface Invoice {
  id: string;
  number: string;
  plan: { code: string; name: string };
  periodStart: string;
  periodEnd: string;
  amount: string;
  currency: string;
  status: 'issued' | 'paid' | 'void';
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
  paymentMethod: string | null;
  paymentReference: string | null;
  notes: string | null;
  tenant?: { id: string; name: string };
  voidReason?: string | null;
}

export interface TenantBilling {
  subscription: {
    id: string;
    status: string;
    readOnly: boolean;
    startsAt: string;
    trialEndsAt: string | null;
    currentPeriodEndsAt: string | null;
    graceEndsAt: string | null;
    plan: { code: string; name: string; priceAmount: string | null; currency: string; billingInterval: string };
  } | null;
  usage: Record<string, { used: number; limit: number | null }>;
  modules: Record<string, boolean>;
  pendingChange: PlanChange | null;
}

export interface SignupConfig {
  enabled: boolean;
  trialDays: number;
  plan: { code: string; name: string } | null;
  plans: PublicPlan[];
  termsUrl: string | null;
  privacyUrl: string | null;
  termsVersion: string;
}

export const billingApi = {
  subscription: () => http.get<TenantBilling>('/billing/subscription'),
  plans: () => http.get<PublicPlan[]>('/billing/plans'),
  invoices: () => http.get<Invoice[]>('/billing/invoices'),
  planChanges: () => http.get<PlanChange[]>('/billing/plan-change-requests'),
  requestChange: (body: { planCode: string; note?: string }) =>
    http.post<PlanChange>('/billing/plan-change-requests', body),
  cancelChange: (id: string) => http.post<PlanChange>(`/billing/plan-change-requests/${id}/cancel`),
  signupConfig: () => http.get<SignupConfig>('/public/signup/config'),
  signup: (body: Record<string, unknown>) => http.post<{ accepted: boolean }>('/public/signup', body),
  verifySignup: (token: string) =>
    http.post<{ workspace: string; inviteToken: string }>('/public/signup/verify', { token }),
};

export const billingKeys = {
  subscription: ['billing', 'subscription'] as const,
  plans: ['billing', 'plans'] as const,
  invoices: ['billing', 'invoices'] as const,
  changes: ['billing', 'changes'] as const,
  signupConfig: ['signup', 'config'] as const,
};

export const STATUS_LABELS: Record<string, string> = {
  trialing: 'Free trial',
  active: 'Active',
  past_due: 'Payment due',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
  expired: 'Ended (read-only)',
};

export function formatMoney(amount: string | null, currency: string): string {
  if (amount === null) return 'Contact us';
  const n = Number(amount);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${currency} ${amount}`;
  }
}

export const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';
